import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CheckCheck, Trash2, Volume2, VolumeX } from "lucide-react";
import { makeArrivalDetector, playChime, setSoundOn, soundOn } from "@/lib/notifySound";
import { NotificationFeed, NotificationFilters } from "@/components/NotificationFeed";
import { catOf, decideRequest, isPendingRequest, openNotification } from "@/lib/notifications";

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState({ unread: false, cat: "" });
  const boxRef = useRef(null);
  const nav = useNavigate();
  const detect = useRef(makeArrivalDetector()).current;
  const [sound, setSound] = useState(soundOn());
  const load = () => api.get("/notifications").then((r) => {
    const fresh = detect(r.data);
    if (fresh.length) { playChime(); popups(fresh); }
    setItems(r.data);
  }).catch(() => {});
  // small preview pop-ups for new arrivals (max 3, then a summary)
  const popups = (fresh) => {
    fresh.slice(0, 3).forEach((n) => toast(n.title, {
      id: `notif-${n.id}`, description: n.body, duration: 7000, className: "notif-popup",
      action: n.link ? { label: "Open", onClick: () => { setRead(n.id, true); openNotification(n, nav); } } : undefined,
    }));
    if (fresh.length > 3) toast(`${fresh.length - 3} more new notification${fresh.length - 3 === 1 ? "" : "s"}`, { action: { label: "View", onClick: () => setOpen(true) } });
  };
  const toggleSound = () => { const next = !sound; setSoundOn(next); setSound(next); if (next) playChime(true); };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  // Close when clicking anywhere outside the bell/panel, or pressing Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const unread = items.filter((n) => !n.read).length;
  const counts = useMemo(() => items.reduce((a, n) => ({ ...a, [catOf(n)]: (a[catOf(n)] || 0) + 1 }), {}), [items]);
  const shown = items.filter((n) => (!filter.unread || !n.read) && (!filter.cat || catOf(n) === filter.cat)).slice(0, 50);
  const shownUnread = items.some((n) => !n.read && (!filter.cat || catOf(n) === filter.cat));

  const setRead = (id, read) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read } : x)));
  const openItem = (n) => {
    setOpen(false);
    setRead(n.id, true);
    openNotification(n, nav);
  };
  const toggleRead = (n) => { api.put(`/notifications/${n.id}/${n.read ? "unread" : "read"}`).catch(() => {}); setRead(n.id, !n.read); };
  const dismiss = async (n) => {
    try { await api.delete(`/notifications/${n.id}`); setItems((xs) => xs.filter((x) => x.id !== n.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  const markAll = async () => {
    await api.put("/notifications/read-all", null, { params: { category: filter.cat || undefined } }).catch(() => {});
    setItems((xs) => xs.map((x) => (!filter.cat || catOf(x) === filter.cat ? { ...x, read: true } : x)));
  };
  const clearRead = async () => {
    if (!window.confirm("Remove all notifications you've already read? This can't be undone.")) return;
    try { await api.post("/notifications/clear-read"); setItems((xs) => xs.filter((x) => !x.read || isPendingRequest(x))); toast.success("Read notifications cleared"); }
    catch { toast.error("Could not clear"); }
  };
  const act = async (n, which) => { if (await decideRequest(n, which)) load(); };

  return (
    <div className="relative" ref={boxRef}>
      <button data-testid="notification-bell" onClick={() => { setOpen((o) => !o); if (!open) load(); }} className="relative h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
        <Bell className="h-[18px] w-[18px] text-slate-600" />
        {unread > 0 && <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
          <div className="absolute right-0 mt-2 w-[400px] max-w-[90vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50 animate-fadeup" data-testid="notification-panel">
            <div className="px-4 pt-3 pb-2.5 border-b border-slate-100 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-head font-bold text-slate-900 text-sm inline-flex items-center gap-1.5">Notifications
                  <button type="button" data-testid="notification-sound-toggle" onClick={toggleSound} title={sound ? "Sound on — click to mute" : "Sound off — click to turn on"}
                    className={`h-6 w-6 rounded-md inline-flex items-center justify-center hover:bg-slate-100 ${sound ? "text-primary" : "text-slate-400"}`}>
                    {sound ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                  </button>
                </span>
                <div className="flex items-center gap-3">
                  <button data-testid="notification-mark-all" onClick={markAll} disabled={!shownUnread} className="text-xs font-semibold text-primary inline-flex items-center gap-1 disabled:opacity-40"><CheckCheck className="h-3.5 w-3.5" /> Mark all as read</button>
                  <button data-testid="notification-clear-read" onClick={clearRead} disabled={!items.some((x) => x.read)} className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1 disabled:opacity-40"><Trash2 className="h-3.5 w-3.5" /> Clear read</button>
                </div>
              </div>
              <NotificationFilters filter={filter} setFilter={setFilter} counts={counts} testPrefix="bell" />
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              <NotificationFeed items={shown} onOpen={openItem} onToggleRead={toggleRead} onDismiss={dismiss} onAct={act}
                empty={items.length ? "No notifications match this filter." : "You're all caught up."} />
            </div>
            <Link to="/admin/notifications" data-testid="notification-view-all" onClick={() => setOpen(false)} className="block text-center text-xs font-semibold text-primary py-2.5 border-t border-slate-100 hover:bg-slate-50">View all notifications</Link>
          </div>
      )}
    </div>
  );
}
